import {useI18n} from '@udemy/i18n';
import {SpotIllustration} from '@udemy/illustrations';
import {Button} from '@udemy/react-core-components';
import {Modal} from '@udemy/react-dialog-components';
import {Checkbox} from '@udemy/react-form-components';
import {TakeOverWizard} from '@udemy/react-structure-components';

import {type BannerAssessment} from '../assessments-banner/assessments-banner-card.react-component';
import styles from '../pop-quiz-modal.module.less';
import {QuizData} from './types';
import {renderUdemyLogo} from './utils';

interface PopQuizPromptPhaseProps {
    isOpen: boolean;
    quizData: QuizData;
    stopShowing: boolean;
    setStopShowing: (v: boolean) => void;
    onAccept: () => void;
    onDismiss: () => void;
    recommendedAssessment?: BannerAssessment | null;
    onRecommendedAssessmentSelected?: () => void;
}

export function PopQuizPromptPhase({
    isOpen,
    quizData,
    stopShowing,
    setStopShowing,
    onAccept,
    onDismiss,
    recommendedAssessment,
    onRecommendedAssessmentSelected,
}: PopQuizPromptPhaseProps) {
    const {gettext, interpolate} = useI18n();

    return (
        <Modal
            isOpen={isOpen}
            title=""
            renderTitle={() => [
                'pop-quiz-prompt-label',
                <span key="t" id="pop-quiz-prompt-label" className="ud-sr-only">
                    {gettext('Pop quiz')}
                </span>,
            ]}
            className={styles['quiz-modal']}
            requireExplicitAction={true}
        >
            <TakeOverWizard
                fullScreenHeight={false}
                headerProps={{
                    renderLogo: renderUdemyLogo,
                    centerChildren: gettext('Knowledge check'),
                    rightChildren: (
                        <Button udStyle="ghost" size="medium" onClick={onDismiss}>
                            {gettext('Exit')}
                        </Button>
                    ),
                }}
                footerProps={{
                    leftChildren: (
                        <Checkbox
                            checked={stopShowing}
                            onChange={(e) => setStopShowing(e.target.checked)}
                        >
                            {gettext('Stop showing me this')}
                        </Checkbox>
                    ),
                    rightChildren: (
                        <div className={styles['footer-actions']}>
                            <Button udStyle="ghost" size="medium" onClick={onDismiss}>
                                {gettext('Not now')}
                            </Button>
                            <Button udStyle="primary" size="medium" onClick={onAccept}>
                                {gettext("Let's go")}
                            </Button>
                        </div>
                    ),
                }}
            >
                <div className={styles['prompt-content']}>
                    <p className="ud-heading-lg">{gettext('Ready for a Knowledge Check?')}</p>
                    <div className={styles['prompt-card']}>
                        <p className="ud-text-md">
                            {interpolate(
                                gettext('See what you remember from %(courseTitle)s'),
                                {courseTitle: quizData.courseTitle},
                                true,
                            )}
                        </p>
                        <SpotIllustration name="growth" width={120} height={120} alt="" />
                    </div>
                    {recommendedAssessment && (
                        <div className={styles['assessment-panel']}>
                            <p className="ud-text-md">
                                {interpolate(
                                    gettext(
                                        'Ready for a Deeper Dive? Try our %(assessmentTitle)s Assessment to test your skills',
                                    ),
                                    {assessmentTitle: recommendedAssessment.title},
                                    true,
                                )}
                            </p>
                            <Button
                                componentClass="a"
                                href={recommendedAssessment.urlLanding}
                                target="_blank"
                                rel="noopener noreferrer"
                                udStyle="ghost"
                                size="medium"
                                onClick={onRecommendedAssessmentSelected}
                            >
                                {gettext('Get started')}
                            </Button>
                        </div>
                    )}
                </div>
            </TakeOverWizard>
        </Modal>
    );
}
